// Copied and adapted from TeacherCustomizeExamPage.tsx
import React, { useState, useEffect, useRef } from 'react';
import { asList } from '../../../utils/apiList';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../../components/layout/MainLayout';
import TeacherSidebar from '../sidebar/TeacherSidebar';
import { Alert } from '../../../utils/Alert';
import Card from '../../../components/ui/Card';
import Button from '../../../components/ui/Button';
import { useAuth } from '../../../contexts/AuthContext';
import '../Teacher.css';
import '../../admin/Admin.css';
// @ts-ignore
import { v4 as uuidv4 } from 'uuid';
import { Autocomplete, TextField } from '@mui/material';
import ReactModal from 'react-modal';
import Chip from '@mui/material/Chip';


import { API_URL } from '../../../config';
import { renderMathHtml } from '../../../utils/renderMath';
interface TeacherOfflineCustomizeExamPageProps {
  user: {
    name: string;
    role: string;
    avatar?: string;
  };
  onLogout: () => void;
}

interface Course {
  id: number;
  name: string;
  description: string;
}

interface Subject {
  id: number;
  name: string;
  code?: string;
  stream_id?: number;
  stream?: {
    id: number;
    name: string;
    description?: string;
    class_id?: number;
    class_?: {
      id: number;
      name: string;
    }
  };
}

interface Chapter {
  id: number;
  name: string;
  description?: string;
  subject_id?: number;
  subject?: {
    id: number;
    name: string;
  };
}

interface Topic {
  id: number;
  name: string;
  description?: string;
  chapter_id?: number;
  chapter?: {
    id: number;
    name: string;
  };
}

type AssociationType = 'course' | 'subject' | 'chapter' | 'topic';

interface ExamForm {
  title: string;
  description: string;
  scheduledDate: string;
  scheduledTime: string;
  duration_minutes: number;
  max_marks: number;
  max_questions: number;
  selectedIds: number[];
  associationType: AssociationType | '';
  type: string;
  target_exam_id: string | number; // Allow string or number for HTML5 validation
  result_release_mode?: 'immediate' | 'after_window';
}

type InstructionEntry = { id: string; key: string; values: string[] };
type WizardStep = 'details' | 'associations' | 'questionTypeConfig' | 'questions';

function formatValidationErrorMessage(message: string): string {
  // Handle the new error format with difficulty level validation
  if (typeof message === 'object' && message !== null) {
    const errorObj = message as any;
    if (errorObj.message === 'Insufficient questions' && errorObj.errors && Array.isArray(errorObj.errors)) {
      // Extract difficulty level error messages and clean them up
      const difficultyErrors = errorObj.errors.filter((error: string) => 
        error.includes('Sum of difficulty_levels percentages must be 100')
      );
      
      if (difficultyErrors.length > 0) {
        // Clean up the error messages by removing "Invalid filter item: 400:" prefix
        const cleanedErrors = difficultyErrors.map((error: string) => 
          error.replace(/^Invalid filter item: \d+: /, '')
        );
        return `Insufficient questions\n${cleanedErrors.join('\n')}`;
      }
      
      // If no specific difficulty errors, show all errors (cleaned)
      const cleanedErrors = errorObj.errors.map((error: string) => 
        error.replace(/^Invalid filter item: \d+: /, '')
      );
      return `Insufficient questions\n${cleanedErrors.join('\n')}`;
    }
  }
  
  const regex = /Not enough questions of type '([^']+)' in subject '([^']+)' \(needed: (\d+), found: (\d+)\)/;
  const match = message.match(regex);
  if (match) {
    const [, type, subject, needed, found] = match;
    const typeMap: Record<string, string> = {
      'single_correct_1': 'Single Correct',
      'multiple_correct': 'Multiple Correct',
      'integer_type': 'Integer Type',
      'matrix_match': 'Matrix Match',
      'three_column_match': 'Three Column Match',
      'comprehension': 'Comprehension',
      'numeric_type': 'Numeric Type',
      'numerical_type': 'Numerical Type',
    };
    const typeLabel = typeMap[type] || type;
    return `Not enough "${typeLabel}" questions in subject "${subject}".\nNeeded: ${needed}, Found: ${found}.`;
  }
  return message;
}



const TeacherOfflineCustomizeExamPage: React.FC<TeacherOfflineCustomizeExamPageProps> = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [error, setError] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState<WizardStep>('details');
  const [associationError, setAssociationError] = useState<boolean>(false);
  const [showConfigModal, setShowConfigModal] = useState<boolean>(false);
  
  // State for modal table - each row has its own independent configuration
  const [modalRowConfigs, setModalRowConfigs] = useState<Record<string, {
    questionsPerSubject: number;
    correctResponse: number;
    incorrectResponse: number;
    mandatoryQuestions: number;
  }>>({});

  // Add success message states
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const [courses, setCourses] = useState<Course[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [chapters, setChapters] = useState<Chapter[]>([]);
  const [topics, setTopics] = useState<Topic[]>([]);

  // 1. Add targetExams state
  const [targetExams, setTargetExams] = useState<any[]>([]);

  // 2. Update formData to include target_exam_id
  const [formData, setFormData] = useState<ExamForm>({
    title: '',
    description: '',
    scheduledDate: '',
    scheduledTime: '',
    duration_minutes: 60,
    max_marks: 1,
    max_questions: 1,
    selectedIds: [],
    associationType: '',
    type: '', // keep for compatibility, but will use target_exam_id
    target_exam_id: '', // Use empty string for HTML5 validation
    result_release_mode: 'immediate',
  });

  const [instructions, setInstructions] = useState<InstructionEntry[]>([
    { id: uuidv4(), key: '', values: [''] }
  ]);

  const [questionDistribution, setQuestionDistribution] = useState<any[]>([]);

  const [selectedQuestionsMap, setSelectedQuestionsMap] = useState<{ [key: string]: number[] }>({});
  const [associationQuestionsMap, setAssociationQuestionsMap] = useState<{ [key: string]: any[] }>({});

  const [selectedAssociation, setSelectedAssociation] = useState<{
    type: AssociationType;
    id: number;
    name: string;
  } | null>(null);
  const [associationQuestions, setAssociationQuestions] = useState<any[]>([]);
  const [selectedQuestions, setSelectedQuestions] = useState<number[]>([]);
  const [loadingQuestions, setLoadingQuestions] = useState(false);

  const [questionFilters, setQuestionFilters] = useState({
    difficulty: 'all',
    type: 'all'
  });
  const [filteredQuestions, setFilteredQuestions] = useState<any[]>([]);
  const [expandedQuestions, setExpandedQuestions] = useState<Set<number>>(new Set());

  const [questionTypeConfigs, setQuestionTypeConfigs] = useState([
    {
      id: uuidv4(),
      questionType: 'single_correct_1', // Initialize with offline exam question type
      questionsPerSubject: 0,
      mandatoryQuestions: 0,
      correctResponse: 0,
      incorrectResponse: 0,
      instructions: '',
    },
  ]);
  const [questionTypeConfigError, setQuestionTypeConfigError] = useState<string>('');
  const [hiddenSectionRowKeys, setHiddenSectionRowKeys] = useState<Set<string>>(new Set());

  // Add popupError state for backend error modal
  const [popupError, setPopupError] = useState<string | null>(null);
  const errorRef = useRef<HTMLDivElement | null>(null);
  
  // State for editing difficulty percentages
  const [editingDifficulty, setEditingDifficulty] = useState<{
    isOpen: boolean;
    associationId: number;
    associationName: string;
    associationType: AssociationType;
    currentPercentages: { easy: number; moderate: number; difficult: number };
  } | null>(null);
  const [tempPercentages, setTempPercentages] = useState<{ easy: number; moderate: number; difficult: number }>({ easy: 0, moderate: 0, difficult: 0 });



  useEffect(() => {
    const fetchAllData = async () => {
      try {
        setIsLoading(true);
        const [
          coursesResponse,
          subjectsResponse,
          chaptersResponse,
          topicsResponse
        ] = await Promise.all([
          fetch(`${API_URL}/api/courses/`, {
            headers: {
              'accept': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          }),
          fetch(`${API_URL}/api/subjects/`, {
            headers: {
              'accept': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          }),
          fetch(`${API_URL}/api/chapters/?limit=100000`, {
            headers: {
              'accept': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          }),
          fetch(`${API_URL}/api/topics/?limit=100000`, {
            headers: {
              'accept': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          })
        ]);
        if (!coursesResponse.ok || !subjectsResponse.ok || !chaptersResponse.ok || !topicsResponse.ok) {
          throw new Error('Failed to fetch one or more data types');
        }
        const [coursesData, subjectsData, chaptersData, topicsData] = await Promise.all([
          coursesResponse.json(),
          subjectsResponse.json(),
          chaptersResponse.json(),
          topicsResponse.json()
        ]);
        setCourses(coursesData);
        setSubjects(subjectsData);
        setChapters(asList(chaptersData));
        setTopics(asList(topicsData));
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch data');
      } finally {
        setIsLoading(false);
      }
    };
    fetchAllData();
  }, [token]);

  // Scroll to error when it appears
  useEffect(() => {
    if (error) {
      errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [error]);

  // 3. Fetch target exams on mount
  useEffect(() => {
    const fetchTargetExams = async () => {
      try {
        const response = await fetch(`${API_URL}/api/target-exams/`, {
          headers: {
            'accept': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });
        if (response.ok) {
          const data = await response.json();
          setTargetExams(data);
        }
      } catch (err) {
        // ignore for now
      }
    };
    fetchTargetExams();
  }, [token]);

  useEffect(() => {
    if (formData.selectedIds.length === 0) {
      setQuestionDistribution([]);
      return;
    }
    const newDistribution = formData.selectedIds.map((id) => {
      const assocKey = `${formData.associationType}-${id}`;
      const questions = associationQuestionsMap[assocKey] || [];
      const total = questions.length;
      const counts = { easy: 0, moderate: 0, difficult: 0 };
      questions.forEach((q: any) => {
        if (q.difficulty_level === 'easy') counts.easy += 1;
        else if (q.difficulty_level === 'moderate') counts.moderate += 1;
        else if (q.difficulty_level === 'difficult') counts.difficult += 1;
      });
      const percent = {
        easy: total ? Math.round((counts.easy / total) * 100) : 0,
        moderate: total ? Math.round((counts.moderate / total) * 100) : 0,
        difficult: total ? Math.round((counts.difficult / total) * 100) : 0,
      };
      let assocObj: any = null;
      switch (formData.associationType) {
        case 'course':
          assocObj = courses.find(c => c.id === id);
          break;
        case 'subject':
          assocObj = subjects.find(s => s.id === id);
          break;
        case 'chapter':
          assocObj = chapters.find(ch => ch.id === id);
          break;
        case 'topic':
          assocObj = topics.find(t => t.id === id);
          break;
        default:
          break;
      }
      return {
        id,
        [formData.associationType]: assocObj,
        difficulty_percent: percent,
      };
    });
    setQuestionDistribution(newDistribution);
  }, [formData.selectedIds, formData.associationType, associationQuestionsMap, courses, subjects, chapters, topics]);

  useEffect(() => {
    formData.selectedIds.forEach(id => {
      const assocKey = `${formData.associationType}-${id}`;
      if (selectedQuestionsMap[assocKey] === undefined || associationQuestionsMap[assocKey] === undefined) {
        let url = '';
        switch (formData.associationType) {
          case 'course': url = `${API_URL}/api/questions/course/${id}`; break;
          case 'subject': url = `${API_URL}/api/questions/subject/${id}`; break;
          case 'chapter': url = `${API_URL}/api/questions/chapter/${id}`; break;
          case 'topic': url = `${API_URL}/api/questions/topic/${id}`; break;
          default: break;
        }
        if (url) {
          fetch(url, {
            headers: {
              'accept': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          })
            .then(res => res.json())
            .then(data => {
              setSelectedQuestionsMap(prev => ({
                ...prev,
                [assocKey]: data.map((q: any) => q.id)
              }));
              setAssociationQuestionsMap(prev => ({
                ...prev,
                [assocKey]: data
              }));
            });
        }
      }
    });
    setSelectedQuestionsMap(prev => {
      const newMap: typeof prev = {};
      formData.selectedIds.forEach(id => {
        const assocKey = `${formData.associationType}-${id}`;
        if (prev[assocKey]) {
          newMap[assocKey] = prev[assocKey];
        }
      });
      return newMap;
    });
    setAssociationQuestionsMap(prev => {
      const newMap: typeof prev = {};
      formData.selectedIds.forEach(id => {
        const assocKey = `${formData.associationType}-${id}`;
        if (prev[assocKey]) {
          newMap[assocKey] = prev[assocKey];
        }
      });
      return newMap;
    });
  }, [formData.selectedIds, formData.associationType, token]);

  useEffect(() => {
    if (associationQuestions.length > 0) {
      let filtered = [...associationQuestions];
      if (questionFilters.difficulty !== 'all') {
        filtered = filtered.filter(q => q.difficulty_level === questionFilters.difficulty);
      }
      if (questionFilters.type !== 'all') {
        filtered = filtered.filter(q => q.type === questionFilters.type);
      }
      setFilteredQuestions(filtered);
    } else {
      setFilteredQuestions([]);
    }
  }, [associationQuestions, questionFilters]);

  // Handler functions
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === 'associationType') {
      setFormData(prev => ({
        ...prev,
        associationType: value as AssociationType,
        selectedIds: []
      }));
      setSelectedQuestionsMap({});
      setSelectedQuestions([]);
    } else if (name === 'target_exam_id') {
      // Handle target_exam_id conversion for HTML5 validation
      setFormData(prev => ({
        ...prev,
        target_exam_id: value === '' ? '' : Number(value)
      }));
      return;
    } else if (name === 'result_release_mode') {
      setFormData(prev => ({
        ...prev,
        result_release_mode: value as 'immediate' | 'after_window'
      }));
      return;
    } else if (name === 'scheduledTime') {
      // Validate time selection for today's date: must be strictly greater than current time
      const selectedDate = formData.scheduledDate;
      const today = new Date().toISOString().split('T')[0];
      if (selectedDate === today) {
        const currentTime = new Date().toTimeString().slice(0, 5);
        if (value <= currentTime) {
          Alert.error('Oops...', 'Scheduled time must be later than the current time for today.');
          return;
        }
      }
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };
  const getAssociationOptions = (): any[] => {
    switch (formData.associationType) {
      case 'course': return courses;
      case 'subject': return subjects;
      case 'chapter': return chapters;
      case 'topic': return topics;
      default: return [];
    }
  };
  const formatOptionName = (item: any) => {
    switch (formData.associationType) {
      case 'course':
        return `${item.name}`;
      case 'subject':
        return `${item.name} (${item.stream?.class_?.name || 'N/A'} - ${item.stream?.name || 'N/A'})`;
      case 'chapter':
        return `${item.name} (${item.subject?.name || 'N/A'})`;
      case 'topic':
        return `${item.name} (${item.chapter?.name || 'N/A'})`;
      default:
        return item.name;
    }
  };
  const handleInstructionKeyChange = (id: string, newKey: string) => {
    setInstructions(prev => prev.map(entry => entry.id === id ? { ...entry, key: newKey } : entry));
  };
  const handleInstructionValueChange = (id: string, idx: number, newValue: string) => {
    setInstructions(prev => prev.map(entry => {
      if (entry.id === id) {
        const newValues = [...entry.values];
        newValues[idx] = newValue;
        return { ...entry, values: newValues };
      }
      return entry;
    }));
  };
  const addInstructionEntry = () => {
    setInstructions(prev => ([...prev, { id: uuidv4(), key: '', values: [''] }]));
  };
  const removeInstructionEntry = (id: string) => {
    setInstructions(prev => prev.filter(entry => entry.id !== id));
  };
  const addInstructionValue = (id: string) => {
    setInstructions(prev => prev.map(entry => entry.id === id ? { ...entry, values: [...entry.values, ''] } : entry));
  };
  const removeInstructionValue = (id: string, idx: number) => {
    setInstructions(prev => prev.map(entry => {
      if (entry.id === id) {
        const newValues = entry.values.filter((_, i) => i !== idx);
        return { ...entry, values: newValues.length ? newValues : [''] };
      }
      return entry;
    }));
  };
  const handleNextStep = () => {
    if (currentStep === 'details') {
      // Inline validation for association type
      if (!formData.associationType) {
        setAssociationError(true);
        return;
      }
      // Inline validation for associations
      if (!formData.selectedIds || formData.selectedIds.length === 0) {
        setAssociationError(true);
        return;
      }
      setAssociationError(false);
      setCurrentStep('questionTypeConfig');
    } else if (currentStep === 'questionTypeConfig') {
      setCurrentStep('questions');
    }
  };
  const handlePreviousStep = () => {
    if (currentStep === 'questionTypeConfig') {
      setCurrentStep('details');
    } else if (currentStep === 'questions') {
      setCurrentStep('questionTypeConfig');
    }
  };
  const handleSelectAssociation = async (type: AssociationType, id: number, name: string) => {
    setSelectedAssociation({ type, id, name });
    setLoadingQuestions(true);
    setQuestionFilters({ difficulty: 'all', type: 'all' });
    setExpandedQuestions(new Set());
    let url = '';
    switch (type) {
      case 'course': url = `${API_URL}/api/questions/course/${id}`; break;
      case 'subject': url = `${API_URL}/api/questions/subject/${id}`; break;
      case 'chapter': url = `${API_URL}/api/questions/chapter/${id}`; break;
      case 'topic': url = `${API_URL}/api/questions/topic/${id}`; break;
      default: break;
    }
    try {
      const response = await fetch(url, {
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      setAssociationQuestions(data);
      const assocKey = `${type}-${id}`;
      setSelectedQuestions(selectedQuestionsMap[assocKey] || []);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching questions:', error);
    } finally {
      setLoadingQuestions(false);
    }
  };
  const handleSaveQuestions = () => {
    if (selectedAssociation) {
      const assocKey = `${selectedAssociation.type}-${selectedAssociation.id}`;
      setSelectedQuestionsMap(prev => ({
        ...prev,
        [assocKey]: selectedQuestions,
      }));
      setAssociationQuestionsMap(prev => ({
        ...prev,
        [assocKey]: associationQuestions.filter((q: any) => selectedQuestions.includes(q.id)),
      }));
      setSelectedAssociation(null);
    }
  };
  const groupQuestionsByDifficulty = (questions: any[]) => {
    const groups: { [key: string]: any[] } = {};
    questions.forEach(q => {
      const level = q.difficulty_level || 'Unknown';
      if (!groups[level]) groups[level] = [];
      groups[level].push(q);
    });
    return groups;
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await submitExam();
  };
  const submitExam = async () => {
    setIsSubmitting(true);

    try {
      // Modal data will be used directly for payload, no sync needed
      // Build filters for new API schema
      type Filter = {
        subjectId: number;
        chapters: number[];
        topics: number[];
        types: Record<string, number>;
        type_marks: Record<string, number>;
        type_negative_marks: Record<string, number>;
        type_mandatory: Record<string, number>;
        difficulty_levels: Record<string, number>;
      };

      const subjectIdToFilter: Record<number, Filter> = {};
      const navStateLocal: any = (window.history.state && (window.history.state as any).usr) || null;

      const ensureFilter = (subjectId: number) => {
        if (!subjectIdToFilter[subjectId]) {
          subjectIdToFilter[subjectId] = {
            subjectId,
            chapters: [],
            topics: [],
            types: {},
            type_marks: {},
            type_negative_marks: {},
            type_mandatory: {},
            difficulty_levels: {},
          };
        }
        return subjectIdToFilter[subjectId];
      };

      if (navStateLocal?.syllabusFilters && Array.isArray(navStateLocal.syllabusFilters) && navStateLocal.syllabusFilters.length > 0) {
        // Use filters built from syllabus page
        navStateLocal.syllabusFilters.forEach((f: any) => {
          const filter: any = ensureFilter(Number(f.subjectId));
          if (Array.isArray(f.chapters)) filter.chapters.push(...f.chapters);
          if (Array.isArray(f.topics)) filter.topics.push(...f.topics);
        });
      } else if (formData.associationType === 'subject') {
        formData.selectedIds.forEach((sid) => { ensureFilter(Number(sid)); });
      } else if (formData.associationType === 'chapter') {
        formData.selectedIds.forEach((cid) => {
          const chapter = chapters.find((c) => c.id === cid);
          const sid = Number(chapter?.subject?.id || 0);
          if (!sid) return;
          const f = ensureFilter(sid);
          if (!f.chapters.includes(cid)) f.chapters.push(cid);
        });
      } else if (formData.associationType === 'topic') {
        formData.selectedIds.forEach((tid) => {
          const topic = topics.find((t) => t.id === tid);
          const chapter = chapters.find((c) => c.id === topic?.chapter?.id);
          const sid = Number(chapter?.subject?.id || 0);
          if (!sid) return;
          const f = ensureFilter(sid);
          if (topic?.chapter?.id && !f.chapters.includes(topic.chapter.id)) {
            f.chapters.push(topic.chapter.id);
          }
          if (!f.topics.includes(tid)) f.topics.push(tid);
        });
      }

      // Process question type configurations for each subject using modal data
      console.log('=== PROCESSING QUESTION TYPE CONFIGS FROM MODAL ===');
      console.log('Current modalRowConfigs for payload:', modalRowConfigs);
      
      // Get association names for modal row key generation
      const associationNames: string[] = (() => {
        if (formData.associationType === 'subject') {
          return subjects.filter(s => formData.selectedIds.includes(s.id)).map(s => s.name);
        }
        if (formData.associationType === 'chapter') {
          return chapters.filter(c => formData.selectedIds.includes(c.id)).map(c => c.name);
        }
        if (formData.associationType === 'topic') {
          return topics.filter(t => formData.selectedIds.includes(t.id)).map(t => t.name);
        }
        return [];
      })();

      const typeToLabel = (t: string) => (
        t === 'single_correct_1' ? 'Single Correct' :
        t === 'multiple_correct' ? 'Multiple Correct' :
        t === 'integer_type' ? 'Integer Type' :
        t === 'matrix_match' ? 'Matrix Match' :
        t === 'three_column_match' ? 'Three Column Match' :
        t === 'comprehension' ? 'Comprehension' :
        t === 'numeric_type' ? 'Numeric Type' :
        t === 'numerical_type' ? 'Numerical Type' : t.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())
      );

      // Process modal data - group by subject and use standard question type keys
      // Clear existing filters and create new ones based on modal data
      const newFilters: Filter[] = [];
      
      // Group modal data by subject and question type
      const subjectQuestionTotals: Record<number, Record<string, {
        questionsPerSubject: number;
        correctResponse: number;
        incorrectResponse: number;
        mandatoryQuestions: number;
      }>> = {};
      
      // Process each modal row individually to preserve exact values
      Object.entries(modalRowConfigs).forEach(([rowKey, config]) => {
        if (config.questionsPerSubject > 0) {
          // Extract association name and question type from row key
          const associationName = associationNames.find(name => rowKey.includes(name));
          const questionType = questionTypeConfigs.find(cfg => rowKey.includes(typeToLabel(cfg.questionType)))?.questionType;
          
          if (associationName && questionType) {
            // Find the subject ID for this association
            let subjectId: number | null = null;
            
            if (formData.associationType === 'subject') {
              const subject = subjects.find(s => s.name === associationName);
              subjectId = subject?.id || null;
            } else if (formData.associationType === 'chapter') {
              const chapter = chapters.find(c => c.name === associationName);
              subjectId = chapter?.subject_id || null;
            } else if (formData.associationType === 'topic') {
              const topic = topics.find(t => t.name === associationName);
              if (topic?.chapter_id) {
                const chapter = chapters.find(c => c.id === topic.chapter_id);
                subjectId = chapter?.subject_id || null;
              }
            }
            
            if (subjectId) {
              // Initialize subject totals if not exists
              if (!subjectQuestionTotals[subjectId]) {
                subjectQuestionTotals[subjectId] = {};
              }
              
              // Initialize question type totals if not exists
              if (!subjectQuestionTotals[subjectId][questionType]) {
                subjectQuestionTotals[subjectId][questionType] = {
                  questionsPerSubject: 0,
                  correctResponse: 0,
                  incorrectResponse: 0,
                  mandatoryQuestions: 0,
                };
              }
              
              // Add values (this will sum up all associations for the same subject and question type)
              subjectQuestionTotals[subjectId][questionType].questionsPerSubject += config.questionsPerSubject;
              subjectQuestionTotals[subjectId][questionType].correctResponse += config.correctResponse;
              subjectQuestionTotals[subjectId][questionType].incorrectResponse += config.incorrectResponse;
              subjectQuestionTotals[subjectId][questionType].mandatoryQuestions += config.mandatoryQuestions;
              
              console.log(`Added to subject ${subjectId} totals - ${questionType}: ${config.questionsPerSubject} questions from ${associationName}`);
            }
          }
        }
      });
      
      // Create filters from the grouped totals
      Object.entries(subjectQuestionTotals).forEach(([subjectIdStr, questionTypes]) => {
        const subjectId = Number(subjectIdStr);
        const filter: Filter = {
          subjectId,
          chapters: [],
          topics: [],
          types: {},
          type_marks: {},
          type_negative_marks: {},
          type_mandatory: {},
          difficulty_levels: {},
        };
        
        // Add each question type with standard keys
        Object.entries(questionTypes).forEach(([questionType, totals]) => {
          filter.types[questionType] = totals.questionsPerSubject;
          filter.type_marks[questionType] = totals.correctResponse;
          filter.type_negative_marks[questionType] = Math.abs(totals.incorrectResponse);
          filter.type_mandatory[questionType] = totals.mandatoryQuestions;
          
          console.log(`Added to filter - subject ${subjectId}, ${questionType}: ${totals.questionsPerSubject} questions, ${totals.correctResponse} marks`);
        });
        
        newFilters.push(filter);
      });
      
      // Replace the existing filters with the new ones
      Object.keys(subjectIdToFilter).forEach(key => delete subjectIdToFilter[Number(key)]);
      newFilters.forEach(filter => {
        subjectIdToFilter[filter.subjectId] = filter;
      });

      // Process difficulty levels from question distribution
      Object.values(subjectIdToFilter).forEach((filter) => {
        // Find the corresponding question distribution for this subject
        const distribution = questionDistribution.find((dist: any) => {
          if (dist.subject && dist.subject.id === filter.subjectId) return true;
          if (dist.course && dist.course.id === filter.subjectId) return true;
          if (dist.chapter && dist.chapter.subject_id === filter.subjectId) return true;
          if (dist.topic && dist.topic.chapter?.subject_id === filter.subjectId) return true;
          return false;
        });

        if (distribution && distribution.difficulty_percent) {
          const percent = distribution.difficulty_percent;
          // Only add difficulty levels that have non-zero percentages
          if (percent.easy > 0) filter.difficulty_levels.easy = percent.easy;
          if (percent.moderate > 0) filter.difficulty_levels.moderate = percent.moderate;
          if (percent.difficult > 0) filter.difficulty_levels.difficult = percent.difficult;
        }
      });

      const filters = Object.values(subjectIdToFilter);
      const subject_ids = filters.map((f) => f.subjectId);

      const examData: any = {
        title: formData.title,
        is_draft: false, // Offline exams are never drafts
        subject_ids,
        max_marks: 0,
        target_exam_id: typeof formData.target_exam_id === 'string' ? Number(formData.target_exam_id) : formData.target_exam_id,
        config: { filters },
      };

      // Include scheduling fields for offline exams
      if (formData.duration_minutes) {
        examData.duration_minutes = formData.duration_minutes;
      }
      if (formData.scheduledDate && formData.scheduledTime) {
        const startDateTime = new Date(`${formData.scheduledDate}T${formData.scheduledTime}`);
        examData.start_datetime = startDateTime.toISOString();
      }
      
      const response = await fetch(`${API_URL}/api/exams/exams/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'accept': 'application/json',
        },
        body: JSON.stringify(examData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        let message = errorData.detail || 'Failed to create offline exam';
        if (typeof message !== 'string' && message) {
          if (Array.isArray(message)) {
            message = message.map(m => (typeof m === 'string' ? m : JSON.stringify(m))).join('\n');
          } else if (typeof message === 'object') {
            // Handle the new error format with difficulty level validation
            if (message.message === 'Insufficient questions' && message.errors && Array.isArray(message.errors)) {
              // Extract difficulty level error messages and clean them up
              const difficultyErrors = message.errors.filter((error: string) => 
                error.includes('Sum of difficulty_levels percentages must be 100')
              );
              
              if (difficultyErrors.length > 0) {
                // Clean up the error messages by removing "Invalid filter item: 400:" prefix
                const cleanedErrors = difficultyErrors.map((error: string) => 
                  error.replace(/^Invalid filter item: \d+: /, '')
                );
                message = `Insufficient questions\n${cleanedErrors.join('\n')}`;
              } else {
                // Clean up all error messages
                const cleanedErrors = message.errors.map((error: string) => 
                  error.replace(/^Invalid filter item: \d+: /, '')
                );
                message = `Insufficient questions\n${cleanedErrors.join('\n')}`;
              }
            } else {
              message = message.message || message.msg || JSON.stringify(message);
            }
          }
        }
        setPopupError(formatValidationErrorMessage(message));
        throw new Error(message);
      }

      const newExam = await response.json();
      setSuccessMessage(`Offline exam "${newExam.title}" created successfully!`);
      setShowSuccessNotification(true);
      
      // Auto-hide notification after 3 seconds
      setTimeout(() => {
        setShowSuccessNotification(false);
        setSuccessMessage('');
      }, 3000);
      
      setTimeout(() => {
        navigate(`/teacher/offline-exam/${newExam.id}`);
      }, 1500);
    } catch (err) {
      if (!popupError) {
        let msg = err instanceof Error ? err.message : 'Failed to create offline exam';
        if (typeof msg !== 'string') msg = JSON.stringify(msg);
        setPopupError(formatValidationErrorMessage(msg));
      }
      setError(err instanceof Error ? err.message : 'Failed to create offline exam');
    } finally {
      setIsSubmitting(false);
    }
  };
  const getUniqueQuestionTypes = () => {
    const types = new Set(associationQuestions.map(q => q.type).filter(Boolean));
    return Array.from(types).sort();
  };
  const getUniqueDifficultyLevels = () => {
    const levels = new Set(associationQuestions.map(q => q.difficulty_level).filter(Boolean));
    return Array.from(levels).sort();
  };
  const handleFilterChange = (filterType: 'difficulty' | 'type', value: string) => {
    setQuestionFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };
  const clearFilters = () => {
    setQuestionFilters({
      difficulty: 'all',
      type: 'all'
    });
  };

  // Handler for opening difficulty percentage edit modal
  const handleEditDifficulty = (associationId: number, associationName: string, associationType: AssociationType, currentPercentages: { easy: number; moderate: number; difficult: number }) => {
    setEditingDifficulty({
      isOpen: true,
      associationId,
      associationName,
      associationType,
      currentPercentages
    });
    setTempPercentages({ ...currentPercentages });
  };

  // Handler for closing difficulty percentage edit modal
  const handleCloseDifficultyEdit = () => {
    setEditingDifficulty(null);
    setTempPercentages({ easy: 0, moderate: 0, difficult: 0 });
  };

  // Handler for saving difficulty percentage changes
  const handleSaveDifficultyEdit = () => {
    if (!editingDifficulty) return;

    // Validate that percentages add up to 100
    const total = tempPercentages.easy + tempPercentages.moderate + tempPercentages.difficult;
    if (total !== 100) {
      Alert.error('Oops...', 'Difficulty percentages must add up to 100%');
      return;
    }

    // Update the question distribution with new percentages
    setQuestionDistribution(prev => 
      prev.map(item => {
        if (item.id === editingDifficulty.associationId) {
          return {
            ...item,
            difficulty_percent: {
              easy: tempPercentages.easy,
              moderate: tempPercentages.moderate,
              difficult: tempPercentages.difficult
            }
          };
        }
        return item;
      })
    );

    handleCloseDifficultyEdit();
  };

  // Handler for updating temporary percentages
  const handleTempPercentageChange = (level: 'easy' | 'moderate' | 'difficult', value: number) => {
    setTempPercentages(prev => ({
      ...prev,
      [level]: Math.max(0, Math.min(100, value))
    }));
  };

  // Initialize modal row configs when modal opens
  const initializeModalRowConfigs = () => {
    const associationNames: string[] = (() => {
      if (formData.associationType === 'subject') {
        return subjects.filter(s => formData.selectedIds.includes(s.id)).map(s => s.name);
      }
      if (formData.associationType === 'chapter') {
        return chapters.filter(c => formData.selectedIds.includes(c.id)).map(c => c.name);
      }
      if (formData.associationType === 'topic') {
        return topics.filter(t => formData.selectedIds.includes(t.id)).map(t => t.name);
      }
      return [];
    })();

    const typeToLabel = (t: string) => (
      t === 'single_correct_A' ? 'Single Correct A' :
      t === 'single_correct_B' ? 'Single Correct B' :
      t === 'single_correct_1' ? 'Single Correct' :
      t === 'multiple_correct' ? 'Multiple Correct' :
      t === 'integer_type' ? 'Integer Type' :
      t === 'numerical_type' ? 'Numerical Type' :
      t === 'matrix_match' ? 'Matrix Match' :
      t === 'three_column_match' ? 'Three Column Match' :
      t === 'comprehension' ? 'Comprehension' : t.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())
    );

    const newModalConfigs: Record<string, {
      questionsPerSubject: number;
      correctResponse: number;
      incorrectResponse: number;
      mandatoryQuestions: number;
    }> = {};

    // Create configs for each combination of association and question type using the same key format as modal table
    associationNames.forEach(associationName => {
      questionTypeConfigs.forEach(cfg => {
        const display = `${associationName} ${typeToLabel(cfg.questionType)}`;
        const rowKey = `${display}__${cfg.id}`;
        newModalConfigs[rowKey] = {
          questionsPerSubject: cfg.questionsPerSubject,
          correctResponse: cfg.correctResponse,
          incorrectResponse: cfg.incorrectResponse,
          mandatoryQuestions: cfg.mandatoryQuestions,
        };
      });
    });

    setModalRowConfigs(newModalConfigs);
  };

  // Sync modal changes back to main question type configs
  const syncModalChangesToMainConfig = () => {
    const updatedConfigs = [...questionTypeConfigs];
    
    // Get the section rows to match the same key format used in the modal
    const sectionRows = (() => {
      const associationNames: string[] = (() => {
        if (formData.associationType === 'subject') {
          return subjects.filter(s => formData.selectedIds.includes(s.id)).map(s => s.name);
        }
        if (formData.associationType === 'chapter') {
          return chapters.filter(c => formData.selectedIds.includes(c.id)).map(c => c.name);
        }
        if (formData.associationType === 'topic') {
          return topics.filter(t => formData.selectedIds.includes(t.id)).map(t => t.name);
        }
        return [];
      })();

      const typeToLabel = (t: string) => (
        t === 'single_correct_A' ? 'Single Correct A' :
        t === 'single_correct_B' ? 'Single Correct B' :
        t === 'single_correct_1' ? 'Single Correct' :
        t === 'multiple_correct' ? 'Multiple Correct' :
        t === 'integer_type' ? 'Integer Type' :
        t === 'numerical_type' ? 'Numerical Type' :
        t === 'matrix_match' ? 'Matrix Match' :
        t === 'three_column_match' ? 'Three Column Match' :
        t === 'comprehension' ? 'Comprehension' : t.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())
      );

      return (associationNames.length ? associationNames : [''])
        .flatMap(name => questionTypeConfigs.map(cfg => ({ 
          display: `${name ? name + ' ' : ''}${typeToLabel(cfg.questionType)}`, 
          cfg 
        })));
    })();

    // Update each question type config with the average of all its modal row values
    questionTypeConfigs.forEach(cfg => {
      // Find all rows for this question type config
      const rowsForThisConfig = sectionRows.filter(row => row.cfg.id === cfg.id);
      
      // Collect all modal values for this question type
      const modalValues = rowsForThisConfig.map(row => {
        const rowKey = `${row.display}__${row.cfg.id}`;
        return modalRowConfigs[rowKey];
      }).filter(Boolean);

      if (modalValues.length > 0) {
        // Calculate average values
        const avgQuestionsPerSubject = Math.round(
          modalValues.reduce((sum, val) => sum + val.questionsPerSubject, 0) / modalValues.length
        );
        const avgCorrectResponse = Math.round(
          modalValues.reduce((sum, val) => sum + val.correctResponse, 0) / modalValues.length
        );
        const avgIncorrectResponse = Math.round(
          modalValues.reduce((sum, val) => sum + val.incorrectResponse, 0) / modalValues.length
        );
        const avgMandatoryQuestions = Math.round(
          modalValues.reduce((sum, val) => sum + val.mandatoryQuestions, 0) / modalValues.length
        );

        // Update the config
        const configIndex = updatedConfigs.findIndex(c => c.id === cfg.id);
        if (configIndex !== -1) {
          updatedConfigs[configIndex] = {
            ...updatedConfigs[configIndex],
            questionsPerSubject: avgQuestionsPerSubject,
            correctResponse: avgCorrectResponse,
            incorrectResponse: avgIncorrectResponse,
            mandatoryQuestions: avgMandatoryQuestions,
          };
        }
      }
    });

    setQuestionTypeConfigs(updatedConfigs);
  };
  const toggleQuestionExpansion = (questionId: number) => {
    setExpandedQuestions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(questionId)) {
        newSet.delete(questionId);
      } else {
        newSet.add(questionId);
      }
      return newSet;
    });
  };


  // Jump to questions step if returning from Customize Syllabus
  useEffect(() => {
    const navState = (window.history.state && (window.history.state as any).usr) || null;
    console.log('=== TEACHER OFFLINE EXAM PAGE NAVIGATION EFFECT ===');
    console.log('Navigation state:', navState);
    if (navState && navState.proceedToQuestions === true) {
      console.log('✅ Proceeding to questions step');
      // Restore form data from navigation state if available
      if (navState.formData) {
        console.log('Restoring form data:', navState.formData);
        setFormData(navState.formData);
        (window as any).__examFormData = navState.formData;
      }
      if (navState.questionTypeConfigs) {
        console.log('Restoring question type configs:', navState.questionTypeConfigs);
        setQuestionTypeConfigs(navState.questionTypeConfigs);
        (window as any).__examQuestionTypeConfigs = navState.questionTypeConfigs;
      }
      setCurrentStep('questions');
    } else if (navState && navState.returnFromSyllabus === true) {
      console.log('✅ Returning from syllabus page to question configuration step');
      if (navState.formData) {
        console.log('Restoring form data:', navState.formData);
        setFormData(navState.formData);
        (window as any).__examFormData = navState.formData;
      }
      if (navState.questionTypeConfigs) {
        console.log('Restoring question type configs:', navState.questionTypeConfigs);
        setQuestionTypeConfigs(navState.questionTypeConfigs);
        (window as any).__examQuestionTypeConfigs = navState.questionTypeConfigs;
      }
      if (navState.associationType) { setFormData(prev => ({ ...prev, associationType: navState.associationType })); }
      if (navState.selectedIds) { setFormData(prev => ({ ...prev, selectedIds: navState.selectedIds })); }
      if (navState.subjects) { setSubjects(navState.subjects); }
      if (navState.chapters) { setChapters(navState.chapters); }
      if (navState.topics) { setTopics(navState.topics); }
      setCurrentStep('questionTypeConfig');
    } else {
      console.log('❌ No special navigation flags, staying on current step');
    }
  }, []);
  // Add these helpers above renderQuestionDetails
  const renderQuestionData = (data: any, type: string, question: any) => {
    if (!data) return null;
    switch (type) {
      case 'single_correct_1':
        if (Array.isArray(question.answers) && question.answers.length > 0) {
          return (
            <div className="space-y-2">
              {question.answers.map((ans: any, idx: number) => (
                <div key={ans.id || idx} className="flex items-center space-x-2">
                  {ans.image_url && (
                    <img src={ans.image_url} alt={`Option ${ans.option_label}`} className="w-8 h-8 object-contain rounded" />
                  )}
                  <span className="w-6 h-6 rounded-full border-2 border-gray-300 flex items-center justify-center text-xs">
                    {ans.option_label || String.fromCharCode(65 + idx)}
                  </span>
                  <span className="text-sm" dangerouslySetInnerHTML={{ __html: renderMathHtml(ans.content) }} />
                  {ans.is_correct && <span className="text-green-600 text-xs font-bold">Correct</span>}
                </div>
              ))}
            </div>
          );
        }
        if (question.question_data?.options) {
          return (
            <div className="space-y-2">
              {question.question_data.options.map((option: string, idx: number) => (
                <div key={idx} className="flex items-center space-x-2">
                  <span className="w-6 h-6 rounded-full border-2 border-gray-300 flex items-center justify-center text-xs">
                    {String.fromCharCode(65 + idx)}
                  </span>
                  <span className="text-sm">{option}</span>
                </div>
              ))}
            </div>
          );
        }
        return <div className="text-gray-400 text-xs">No options available.</div>;
      default:
        return (
          <div className="bg-white p-3 rounded border">
            <pre className="text-xs overflow-x-auto">
              {JSON.stringify(data, null, 2)}
            </pre>
          </div>
        );
    }
  };
  const renderCorrectAnswer = (data: any, type: string, question: any) => {
    if (!data) return null;
    switch (type) {
      case 'single_correct_1':
        if (question.is_image_based && Array.isArray(question.answers) && question.answers.length > 0) {
          const correct = question.answers.find((ans: any) => ans.is_correct);
          return (
            <div className="flex flex-col items-center">
              {correct && (
                <>
                  <img src={correct.image_url} alt={`Correct Option`} className="w-20 h-20 object-contain  rounded" />
                  <span className="text-green-700 font-bold">Correct: {correct.option_label}</span>
                </>
              )}
            </div>
          );
        }
        return (
          <div className="flex items-center space-x-2">
            <span className="text-green-700 font-medium">Correct Answer:</span>
            <span className="px-2 py-1 bg-green-100 text-green-800 rounded">
              {typeof data.correct_option === 'number' ? String.fromCharCode(65 + data.correct_option) : data.correct_option}
            </span>
          </div>
        );
      default:
        return null;
    }
  };
  // Update renderQuestionDetails to use these helpers
  const renderQuestionDetails = (question: any) => {
    const isExpanded = expandedQuestions.has(question.id);
    return (
      <div key={question.id} className={`border rounded-lg overflow-hidden mb-2 transition bg-white hover:bg-gray-50 ${selectedQuestions.includes(question.id) ? 'ring-2 ring-blue-200' : ''}`}>
        {/* Question Header - Compact */}
        <div className="flex items-center px-3 py-2 border-b">
          <input
            type="checkbox"
            checked={selectedQuestions.includes(question.id)}
            onChange={() => {
              setSelectedQuestions(prev =>
                prev.includes(question.id)
                  ? prev.filter(id => id !== question.id)
                  : [...prev, question.id]
              );
            }}
            className="mr-2"
          />
          <div className="flex-1 truncate font-medium text-sm" style={{maxWidth: '40vw'}}>
            <span className="truncate" dangerouslySetInnerHTML={{
              __html: renderMathHtml(question.content || question.text || question.question_text || question.title || `Question #${question.id}`)
            }} />
          </div>
          {question.type && (
            <span className="ml-2 px-2 py-0.5 text-xs rounded bg-blue-100 text-blue-800 whitespace-nowrap">Single Correct</span>
          )}
          {question.difficulty_level && (
            <span className={`ml-2 px-2 py-0.5 text-xs rounded whitespace-nowrap ${
              question.difficulty_level === 'easy' ? 'bg-green-100 text-green-800' :
              question.difficulty_level === 'moderate' ? 'bg-yellow-100 text-yellow-800' :
              'bg-red-100 text-red-800'
            }`}>
              {question.difficulty_level.charAt(0).toUpperCase() + question.difficulty_level.slice(1)}
            </span>
          )}
          <button
            onClick={() => toggleQuestionExpansion(question.id)}
            className="ml-2 text-[#111827] hover:text-blue-800 text-sm font-medium flex items-center focus:outline-none"
            tabIndex={0}
            aria-label={isExpanded ? 'Hide Details' : 'Show Details'}
          >
            {isExpanded ? <span>▼</span> : <span>▶</span>}
          </button>
        </div>
        {/* Expanded Question Details */}
        {isExpanded && (
          <div className="p-4 bg-gray-50 border-t">
            <div className="space-y-4">
              {/* Question Image */}
              {question.image_url && (
                <div>
                  <h5 className="font-medium text-gray-600 mb-2">Question Image:</h5>
                  <div className="flex justify-center">
                    <img 
                      src={question.image_url} 
                      alt="Question" 
                      className="max-w-full max-h-64 object-contain rounded border"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </div>
                </div>
              )}
              {/* Question Data (for options) */}
              {question.question_data && (
                <div>
                  <h5 className="font-medium text-gray-600 mb-2">Question Options:</h5>
                  <div className="bg-white p-4 rounded border">
                    {renderQuestionData(question.question_data, question.type, question)}
                  </div>
                </div>
              )}
              {/* Correct Answer Data */}
              {question.correct_answer_data && (
                <div>
                  <h5 className="font-medium text-gray-600 mb-2">Correct Answer:</h5>
                  <div className="bg-white p-4 rounded border">
                    {renderCorrectAnswer(question.correct_answer_data, question.type, question)}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };
  return (
    <MainLayout user={user} onLogout={onLogout} sidebarContent={<TeacherSidebar />}>
      {/* Error Popup Modal */}
      <ReactModal
        isOpen={!!popupError}
        onRequestClose={() => setPopupError(null)}
        className="fixed inset-0 flex items-center justify-center z-50"
        overlayClassName=""
        ariaHideApp={false}
        shouldCloseOnOverlayClick={true}
        style={{ overlay: { backgroundColor: 'rgba(0,0,0,0.2)' }, content: { background: 'none', border: 'none', padding: 0 } }}
      >
        <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md relative flex flex-col items-center">
          <button
            className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 text-2xl font-bold transition"
            onClick={() => setPopupError(null)}
            aria-label="Close"
            style={{ background: 'none', border: 'none', cursor: 'pointer', lineHeight: 1 }}
          >
            ×
          </button>
          <h2 className="text-xl font-bold mb-4 text-center text-red-600">Error</h2>
          <div className="text-base text-gray-900 text-center whitespace-pre-line">{popupError}</div>
        </div>
      </ReactModal>
             {/* Success Notification */}
       {showSuccessNotification && (
         <div className="fixed top-4 right-4 z-[9999] max-w-sm w-full bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded shadow-lg animate-fade-in">
           <div className="flex items-start">
             <div className="flex-shrink-0">
               <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                 <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
               </svg>
             </div>
             <div className="ml-3 flex-1">
               <p className="text-sm font-medium text-green-800">{successMessage}</p>
             </div>
             <div className="ml-auto pl-3">
               <div className="-mx-1.5 -my-1.5">
                 <button
                   onClick={() => {
                     setShowSuccessNotification(false);
                     setSuccessMessage('');
                   }}
                   className="inline-flex bg-green-50 text-green-500 rounded-full p-1.5 hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-green-600 focus:ring-offset-2 focus:ring-offset-green-50"
                 >
                   <span className="sr-only">Dismiss</span>
                   <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                     <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                   </svg>
                 </button>
               </div>
             </div>
           </div>
         </div>
       )}
      <div className="p-6">
        {/* Header with step indicator */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <h1 className="admin-title">Create Customized Offline Exam</h1>
            <div className="flex items-center space-x-4">
              <div className={`px-4 py-2 rounded ${currentStep === 'details' ? 'bg-[#111827] text-white' : 'bg-gray-200'}`}>Step 1: Details & Associations</div>
              <div className={`px-4 py-2 rounded ${currentStep === 'questionTypeConfig' ? 'bg-[#111827] text-white' : 'bg-gray-200'}`}>Step 2: Question Type Config</div>
              <div className={`px-4 py-2 rounded ${currentStep === 'questions' ? 'bg-[#111827] text-white' : 'bg-gray-200'}`}>Step 3: Questions</div>
            </div>
          </div>
        </div>
        {/* Step 1: Offline Exam Details */}
        {currentStep === 'details' && (
          <Card>
            <form onSubmit={(e) => { const form = e.currentTarget as HTMLFormElement; if (!form.checkValidity()) { e.preventDefault(); form.reportValidity(); return; } e.preventDefault(); handleNextStep(); }} className="p-6 space-y-6">
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-600 ">
                    Offline Exam Title
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 ">
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-600 ">
                      Duration (minutes)
                    </label>
                    <input
                      type="number"
                      name="duration_minutes"
                      value={formData.duration_minutes}
                      onChange={handleInputChange}
                      min="1"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 ">
                      Scheduled Date
                    </label>
                    <input
                      type="date"
                      name="scheduledDate"
                      value={formData.scheduledDate}
                      onChange={handleInputChange}
                      min={new Date().toISOString().split('T')[0]}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      required
                    />
                  </div>
                </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                <div>
                  <label className="block text-sm font-medium text-gray-600 ">
                    Scheduled Time
                  </label>
                  <input
                    type="time"
                    name="scheduledTime"
                    value={formData.scheduledTime}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 ">Target Exam *</label>
                  <select
                    name="target_exam_id"
                    value={formData.target_exam_id}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    required
                  >
                    <option value="">Select Target Exam *</option>
                    {targetExams.map((exam: any) => (
                      <option key={exam.id} value={exam.id}>{exam.name}</option>
                    ))}
                  </select>
                </div>
              </div>
                {/* Associations merged here */}
                <div className="border-t pt-6">
                  <h3 className="text-lg font-semibold mb-4">Offline Exam Associations</h3>
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-600 ">
                        Association Type <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="associationType"
                        value={formData.associationType}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        required
                      >
                        <option value="">Select Association Type *</option>
                        <option value="course">Course</option>
                        <option value="subject">Subject</option>
                        {/* <option value="chapter">Chapter</option>
                        <option value="topic">Topic</option> */}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-600 ">
                        Select Multiple {formData.associationType ? formData.associationType.charAt(0).toUpperCase() + formData.associationType.slice(1) + 's' : 'Associations'}
                      </label>
                      <Autocomplete
                        multiple
                        options={getAssociationOptions()}
                        getOptionLabel={(option) => formatOptionName(option)}
                        value={getAssociationOptions().filter((item: any) => formData.selectedIds.includes(item.id))}
                        onChange={(event, newValue) => {
                          const newIds = newValue.map((item: any) => item.id);
                          setFormData(prev => ({
                            ...prev,
                            selectedIds: newIds
                          }));
                        }}
                        disabled={!formData.associationType}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            placeholder={formData.associationType ? `Select ${formData.associationType}s` : 'Please select an association type first'}
                          />
                        )}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <Chip
                              label={formatOptionName(option)}
                              clickable={false}
                              {...getTagProps({ index })}
                            />
                          ))
                        }
                        renderOption={(props, option, { index }) => (
                          <li {...props} key={option.id || `${option.name}-${index}`}>
                            {formatOptionName(option)}
                          </li>
                        )}
                      />
                      {associationError && (
                        <div className="text-red-600 text-sm mt-1">
                          {!formData.associationType 
                            ? 'Please select an association type.' 
                            : `Please select at least one ${formData.associationType}.`}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex flex-col md:flex-row gap-6 items-start">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-600 ">Instructions</label>
                    <div className="space-y-4">
                      {instructions.map((entry) => (
                        <div key={entry.id} className="border p-3 rounded-md bg-gray-50">
                          <div className="flex items-center gap-2 mb-2">
                            <input
                              type="text"
                              value={entry.key}
                              onChange={e => handleInstructionKeyChange(entry.id, e.target.value)}
                              className="px-2 py-1 border rounded w-1/3"
                              placeholder="Title"
                              // required
                            />
                            <button type="button" onClick={() => removeInstructionEntry(entry.id)} className="text-red-500">Remove</button>
                          </div>
                          {entry.values.map((val, idx) => (
                            <div key={idx} className="flex items-center gap-2 mb-2">
                              <input
                                type="text"
                                value={val}
                                onChange={e => handleInstructionValueChange(entry.id, idx, e.target.value)}
                                className="px-2 py-1 border rounded w-full"
                                placeholder="Instruction"
                              //   required
                              />
                              <button type="button" onClick={() => addInstructionValue(entry.id)} className="text-green-600 font-bold">+</button>
                              {entry.values.length > 1 && (
                                <button type="button" onClick={() => removeInstructionValue(entry.id, idx)} className="text-red-400">Remove</button>
                              )}
                            </div>
                          ))}
                        </div>
                      ))}
                      <button type="button" onClick={addInstructionEntry} className="text-green-600 transition">Add Instruction</button>
                    </div>
                  </div>
                  <div className="w-full md:w-1/3">
                    <label className="block text-sm font-medium text-gray-600 ">Result Release Mode</label>
                    <select
                      name="result_release_mode"
                      value={formData.result_release_mode || 'immediate'}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      required
                    >
                      <option value="immediate">Immediate</option>
                      <option value="after_window">After exam Completion</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="flex justify-between">
                <Button
                  variant="light"
                  onClick={() => navigate('/teacher/offline-exam')}
                  className="bg-gray-200 text-gray-900 px-4 py-2 rounded hover:bg-gray-300 transition font-['Inter']"
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  type="submit"
                  className="bg-[#111827] text-white px-4 py-2 rounded hover:bg-[#111827] transition font-['Inter']"
                >
                  Next: Associations
                </Button>
              </div>
            </form>
          </Card>
        )}
        {/* Step 2: Associations */}
        {currentStep === 'associations' && (
          <Card>
            <div className="p-6">
              <h2 className="text-lg font-semibold mb-4">Offline Exam Associations</h2>
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-600 ">
                    Association Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="associationType"
                    value={formData.associationType}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    required
                  >
                    <option value="">Select Association Type *</option>
                    <option value="course">Course</option>
                    <option value="subject">Subject</option>
                    <option value="chapter">Chapter</option>
                    <option value="topic">Topic</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 ">
                    Select Multiple {formData.associationType ? formData.associationType.charAt(0).toUpperCase() + formData.associationType.slice(1) + 's' : 'Associations'}
                  </label>
                  <Autocomplete
                    multiple
                    options={getAssociationOptions()}
                    getOptionLabel={(option) => formatOptionName(option)}
                    value={getAssociationOptions().filter((item: any) => formData.selectedIds.includes(item.id))}
                    onChange={(event, newValue) => {
                      const newIds = newValue.map((item: any) => item.id);
                      setFormData(prev => ({
                        ...prev,
                        selectedIds: newIds
                      }));
                    }}
                    disabled={!formData.associationType}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        placeholder={formData.associationType ? `Select ${formData.associationType}s` : 'Please select an association type first'}
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          label={formatOptionName(option)}
                          clickable={false}
                          {...getTagProps({ index })}
                        />
                      ))
                    }
                    renderOption={(props, option, { index }) => (
                      <li {...props} key={option.id || `${option.name}-${index}`}>
                        {formatOptionName(option)}
                      </li>
                    )}
                  />
                </div>
                <div className="flex justify-between">
                  <Button
                    variant="light"
                    onClick={handlePreviousStep}
                    className="bg-gray-200 text-gray-900 px-4 py-2 rounded hover:bg-gray-300 transition font-['Inter']"
                    >
                    Previous: Details
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleNextStep}
                    disabled={formData.selectedIds.length === 0}
                    className="bg-[#111827] text-white px-4 py-2 rounded hover:bg-[#111827] transition font-['Inter']"
                  >
                    Next: Question Type Config
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        )}
        {/* Step 3: Question Type Configuration */}
        {currentStep === 'questionTypeConfig' && (
          <Card>
            <div className="p-6">
              <h2 className="text-lg font-semibold mb-4">Offline Exam Question Type Configuration</h2>
              <div className="bg-white rounded shadow p-4 mb-4 border relative">
                <div className="grid grid-cols-3 gap-6 items-center mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600 ">Question Type:</label>
                    <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100">Single Correct</div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 ">Questions/{formData.associationType.charAt(0).toUpperCase() + formData.associationType.slice(1)}:</label>
                    <input
                      type="number"
                      min="1"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      value={questionTypeConfigs[0]?.questionsPerSubject ?? 0}
                      onChange={e => {
                        const val = Number(e.target.value);
                        // Ensure minimum value is 1
                        const validatedVal = val < 1 ? 1 : val;
                        setQuestionTypeConfigs(prev => prev.length === 0 ? prev : prev.map((r, i) => i === 0 ? { ...r, questionsPerSubject: validatedVal } : r));
                      }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 ">Mandatory Questions:</label>
                    <input
                      type="number"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      value={questionTypeConfigs[0]?.mandatoryQuestions ?? 0}
                      onChange={e => {
                        const val = Number(e.target.value);
                        setQuestionTypeConfigs(prev => prev.length === 0 ? prev : prev.map((r, i) => i === 0 ? { ...r, mandatoryQuestions: val } : r));
                      }}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-6 items-center mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600 ">Marking Scheme: Correct Response:</label>
                    <input
                      type="number"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      value={questionTypeConfigs[0]?.correctResponse ?? 0}
                      onChange={e => {
                        const val = Number(e.target.value);
                        setQuestionTypeConfigs(prev => prev.length === 0 ? prev : prev.map((r, i) => i === 0 ? { ...r, correctResponse: val } : r));
                      }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 ">Incorrect Response:</label>
                    <input
                      type="number"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      value={(questionTypeConfigs[0]?.incorrectResponse ?? 0) === 0 ? 0 : -Math.abs(questionTypeConfigs[0]?.incorrectResponse ?? 0)}
                      onChange={e => {
                        const raw = e.target.value;
                        const numeric = raw === '' ? 0 : Number(raw);
                        const negative = numeric === 0 ? 0 : -Math.abs(numeric);
                        setQuestionTypeConfigs(prev => prev.length === 0 ? prev : prev.map((r, i) => i === 0 ? { ...r, incorrectResponse: negative } : r));
                      }}
                    />
                  </div>
                </div>
              </div>
              {questionTypeConfigError && (
                <div className="text-red-500 mb-2">{questionTypeConfigError}</div>
              )}
              <div className="flex justify-between mt-6">
                <Button
                  variant="light"
                  onClick={handlePreviousStep}
                  className="bg-gray-200 text-gray-900 px-4 py-2 rounded hover:bg-gray-300 transition font-['Inter']"
                >
                  Back
                </Button>
                <Button
                  variant="primary"
                  onClick={() => {
                    // Validation: Questions/Type must be minimum 1
                    if (questionTypeConfigs.some(cfg => cfg.questionsPerSubject < 1)) {
                      setQuestionTypeConfigError('Questions/Type should be minimum 1 for all types of questions.');
                      return;
                    }
                    setQuestionTypeConfigError('');
                    setHiddenSectionRowKeys(new Set());
                    initializeModalRowConfigs();
                    setShowConfigModal(true);
                  }}
                  className="bg-[#111827] text-white px-4 py-2 rounded hover:bg-[#111827] transition font-['Inter']"
                >
                  Next
                </Button>
              </div>
            </div>
          </Card>
        )}
        {/* Step 4: Question Assignment */}
        {currentStep === 'questions' && (
          <div className="space-y-6">
            {/* Question Distribution Display */}
            <Card>
              <div className="p-6">
                <h2 className="text-lg font-semibold mb-4">Offline Exam Question Distribution</h2>
                {questionDistribution.length > 0 ? (
                  <div className="flex flex-wrap gap-4">
                    {questionDistribution.map((item: any, idx: number) => {
                      let name = 'Unknown';
                      let type: AssociationType = 'course';
                      let assocId: number | null = null;
                      if (item.course) {
                        name = item.course.name;
                        type = 'course';
                        assocId = item.course.id;
                      } else if (item.subject) {
                        name = item.subject.name;
                        type = 'subject';
                        assocId = item.subject.id;
                      } else if (item.chapter) {
                        name = item.chapter.name;
                        type = 'chapter';
                        assocId = item.chapter.id;
                      } else if (item.topic) {
                        name = item.topic.name;
                        type = 'topic';
                        assocId = item.topic.id;
                      }
                      const percent = item.difficulty_percent || {};
                      const uniqueKey = `${item.id || name}-${idx}`;
                      return (
                        <div key={uniqueKey} className="bg-white rounded shadow p-4 min-w-[200px] border">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-bold">{name}</span>
                            <button
                              onClick={() => handleEditDifficulty(assocId!, name, type, percent)}
                              className="text-[#111827] hover:text-blue-800 p-1 rounded hover:bg-blue-50 transition-colors"
                              title="Edit difficulty percentages"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                          </div>
                          <div>
                            <div className="flex justify-between">
                              <span>Easy</span>
                              <span>{percent.easy ?? 0} %</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Moderate</span>
                              <span>{percent.moderate ?? 0} %</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Difficult</span>
                              <span>{percent.difficult ?? 0} %</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-gray-600">No associations selected. Please go back and select associations.</p>
                )}
                <div className="flex justify-between mt-6">
                  <Button
                    variant="light"
                    onClick={handlePreviousStep}
                    className="bg-gray-200 text-gray-900 px-4 py-2 rounded hover:bg-gray-300 transition font-['Inter']"
                  >
                    Previous: Question Type Config
                  </Button>
                  <Button
                    variant="primary"
                    onClick={() => submitExam()}
                    disabled={isSubmitting || questionDistribution.length === 0}
                    className="bg-[#111827] text-white px-4 py-2 rounded hover:bg-[#111827] transition font-['Inter']"
                  >
                    {isSubmitting ? 'Creating...' : 'Create Offline Exam'}
                  </Button>
                </div>
              </div>
            </Card>
            {/* Question Assignment Modal/Page */}
            {selectedAssociation && (
              <Card>
                <div className="p-6">
                  <h2 className="text-lg font-semibold mb-4">Assign Questions for {selectedAssociation.name}</h2>
                  <div className="flex gap-4 mb-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 ">Difficulty</label>
                      <select
                        value={questionFilters.difficulty}
                        onChange={e => handleFilterChange('difficulty', e.target.value)}
                        className="px-2 py-1 border rounded"
                      >
                        <option value="all">All</option>
                        {getUniqueDifficultyLevels().map(level => (
                          <option key={level} value={level}>{level}</option>
                        ))}
                      </select>
                    </div>
                   
                    <Button variant="secondary" onClick={clearFilters} className="self-end">Clear Filters</Button>
                  </div>
                  {loadingQuestions ? (
                    <div>Loading questions...</div>
                  ) : (
                    !loadingQuestions && (
                      <div className="max-h-96 overflow-y-auto">
                        {filteredQuestions.filter(q => q.type === 'single_correct_1').length === 0 ? (
                          <div className="text-gray-600 text-center py-8">
                            No 'Single Correct' questions found for this association.
                          </div>
                        ) : (
                          filteredQuestions.filter(q => q.type === 'single_correct_1').map(q => renderQuestionDetails(q))
                        )}
                      </div>
                    )
                  )}
                  <div className="flex justify-end gap-2 mt-4">
                    <Button variant="secondary" onClick={() => setSelectedAssociation(null)}>Cancel</Button>
                    <Button variant="primary" onClick={handleSaveQuestions}>Save Selection</Button>
                  </div>
                </div>
              </Card>
            )}
          </div>
        )}
      </div>

      {/* Configuration Summary Modal */}
      <ReactModal
        isOpen={showConfigModal}
        onRequestClose={() => setShowConfigModal(false)}
        className="fixed inset-0 flex items-center justify-center z-50"
        overlayClassName=""
        ariaHideApp={false}
        shouldCloseOnOverlayClick={true}
        style={{ overlay: { backgroundColor: 'rgba(0,0,0,0.5)' }, content: { background: 'none', border: 'none', padding: 0 } }}
      >
        {/* <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4"> */}
        <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[85vh] mx-4 flex flex-col">

          {/* Modal Header */}
          <div className="flex items-center justify-between p-6 border-b">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Test Name: {formData.title}</h2>
              <p className="text-sm text-gray-600 mt-1">Duration: {formData.duration_minutes} minutes</p>
            </div>
            <button
              onClick={() => setShowConfigModal(false)}
              className="text-gray-400 hover:text-gray-600 text-2xl font-bold bg-gray-100 hover:bg-gray-200 rounded-full w-8 h-8 flex items-center justify-center"
              aria-label="Close"
            >
              ×
            </button>
          </div>

          {/* Modal Body */}
          {/* <div className="p-6"> */}
          <div className="p-6 overflow-y-auto">

          {(() => {
              const toTitle = (s: string) => s.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());
              const typeToLabel = (t: string) => (
                t === 'single_correct_A' ? 'Single Correct A' :
                t === 'single_correct_B' ? 'Single Correct B' :
                t === 'single_correct_1' ? 'Single Correct' :
                t === 'multiple_correct' ? 'Multiple Correct' :
                t === 'integer_type' ? 'Integer Type' :
                t === 'numerical_type' ? 'Numerical Type' :
                t === 'matrix_match' ? 'Matrix Match' :
                t === 'three_column_match' ? 'Three Column Match' :
                t === 'comprehension' ? 'Comprehension' : toTitle(t)
              );
              const associationNames: string[] = (() => {
                if (formData.associationType === 'subject') {
                  return subjects.filter(s => formData.selectedIds.includes(s.id)).map(s => s.name);
                }
                if (formData.associationType === 'chapter') {
                  return chapters.filter(c => formData.selectedIds.includes(c.id)).map(c => c.name);
                }
                if (formData.associationType === 'topic') {
                  return topics.filter(t => formData.selectedIds.includes(t.id)).map(t => t.name);
                }
                return [];
              })();
              const sectionRows = (associationNames.length ? associationNames : [''])
                .flatMap(name => questionTypeConfigs.map(cfg => ({ display: `${name ? name + ' ' : ''}${typeToLabel(cfg.questionType)}`, cfg })));
              // expose for tbody via window-like scope
              (window as any)._admin_customize_sectionRows = sectionRows;
              (window as any)._admin_customize_typeToLabel = typeToLabel;
              return null;
            })()}
            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-gray-300">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-600">Sections</th>
                    <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-600">No. of Questions</th>
                    <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-600">Correct</th>
                    <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-600">Incorrect</th>
                    <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-600">Mandatory Questions</th>
                    <th className="border border-gray-300 px-4 py-3 text-center font-semibold text-gray-600">Partial marking</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const names: string[] = (() => {
                      if (formData.associationType === 'subject') return subjects.filter(s => formData.selectedIds.includes(s.id)).map(s => s.name);
                      if (formData.associationType === 'chapter') return chapters.filter(c => formData.selectedIds.includes(c.id)).map(c => c.name);
                      if (formData.associationType === 'topic') return topics.filter(t => formData.selectedIds.includes(t.id)).map(t => t.name);
                      return [];
                    })();
                    const perAssocRows = (names.length ? names : ['']).flatMap((name) => (
                      (questionTypeConfigs || []).filter(Boolean).flatMap((cfg: any) => {
                        if (!cfg) return [];
                        const rowKey = `${name || 'All'}__${cfg.id}`;
                        if (hiddenSectionRowKeys.has(rowKey)) return [];
                        
                        // Get the modal-specific config for this row
                        const modalConfig = modalRowConfigs[rowKey] || {
                          questionsPerSubject: cfg?.questionsPerSubject ?? 0,
                          correctResponse: cfg?.correctResponse ?? 0,
                          incorrectResponse: cfg?.incorrectResponse ?? 0,
                          mandatoryQuestions: cfg?.mandatoryQuestions ?? 0,
                        };
                        
                        return (
                          <tr key={rowKey} className="hover:bg-gray-50">
                            <td className="border border-gray-300 px-4 py-3 text-gray-900">{`${name || (formData.associationType.charAt(0).toUpperCase() + formData.associationType.slice(1))} - Single Correct`}</td>
                            <td className="border border-gray-300 px-4 py-3">
                              <input type="number" value={modalConfig.questionsPerSubject} onChange={(e)=>{const val=Number(e.target.value); setModalRowConfigs(prev=>({...prev, [rowKey]: {...prev[rowKey], questionsPerSubject: val}}));}} className="w-full px-2 py-1 border border-gray-300 rounded text-center" min="1" />
                            </td>
                            <td className="border border-gray-300 px-4 py-3">
                              <input type="number" value={modalConfig.correctResponse} onChange={(e)=>{const val=Number(e.target.value); setModalRowConfigs(prev=>({...prev, [rowKey]: {...prev[rowKey], correctResponse: val}}));}} className="w-full px-2 py-1 border border-gray-300 rounded text-center" min="0" />
                            </td>
                            <td className="border border-gray-300 px-4 py-3">
                              <input type="number" value={modalConfig.incorrectResponse === 0 ? 0 : -Math.abs(modalConfig.incorrectResponse)} onChange={(e)=>{const raw=e.target.value; const numeric=raw===''?0:Number(raw); const negative=numeric===0?0:-Math.abs(numeric); setModalRowConfigs(prev=>({...prev, [rowKey]: {...prev[rowKey], incorrectResponse: negative}}));}} className="w-full px-2 py-1 border border-gray-300 rounded text-center" />
                            </td>
                            <td className="border border-gray-300 px-4 py-3">
                              <input type="number" value={modalConfig.mandatoryQuestions} onChange={(e)=>{const val=Number(e.target.value); setModalRowConfigs(prev=>({...prev, [rowKey]: {...prev[rowKey], mandatoryQuestions: val}}));}} className="w-full px-2 py-1 border border-gray-300 rounded text-center" min="0" />
                            </td>
                            <td className="border border-gray-300 px-4 py-3 text-center">
                              <button type="button" onClick={()=> setHiddenSectionRowKeys(prev=>{const next=new Set(prev); next.add(rowKey); return next;})} className="bg-red-100 hover:bg-red-200 text-red-700 rounded p-1" aria-label="Remove Question Type">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    ));
                    return perAssocRows;
                  })()}
                </tbody>
              </table>
            </div>
          </div>

          {/* Modal Footer */}
          <div className="flex justify-between items-center p-6 border-t bg-gray-50">
            <Button
              variant="light"
              // onClick={() => setShowConfigModal(false)}
              onClick={() => {
                setShowConfigModal(false);
                // Store current form data globally so it can be retrieved when returning
                (window as any).__examFormData = formData;
                (window as any).__examQuestionTypeConfigs = questionTypeConfigs;
                
                navigate('/teacher/customize/syllabus', {
                  state: {
                    associationType: formData.associationType,
                    selectedIds: formData.selectedIds,
                    subjects,
                    chapters,
                    topics,
                    title: formData.title,
                    isOffline: true,
                  }
                });
              }}
              className="bg-gray-200 text-gray-900 px-4 py-2 rounded hover:bg-gray-300 transition"
            >
              Customize Syllabus
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setShowConfigModal(false);
                handleNextStep();
              }}
              className="bg-[#111827] text-white px-4 py-2 rounded hover:bg-gray-800 transition"
            >
              Create Full Test
            </Button>
          </div>
        </div>
      </ReactModal>

      {/* Difficulty Percentage Edit Modal */}
      <ReactModal
        isOpen={editingDifficulty?.isOpen || false}
        onRequestClose={handleCloseDifficultyEdit}
        className="fixed inset-0 flex items-center justify-center z-50"
        overlayClassName=""
        ariaHideApp={false}
        shouldCloseOnOverlayClick={true}
        style={{ overlay: { backgroundColor: 'rgba(0,0,0,0.5)' }, content: { background: 'none', border: 'none', padding: 0 } }}
      >
        <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
          <div className="flex items-center justify-between p-6 border-b">
            <h2 className="text-xl font-bold text-gray-900">
              Edit Difficulty Percentages
            </h2>
            <button
              onClick={handleCloseDifficultyEdit}
              className="text-gray-400 hover:text-gray-600 text-2xl font-bold bg-gray-100 hover:bg-gray-200 rounded-lg-full w-8 h-8 flex items-center justify-center shadow-md transition active:scale-95"
              aria-label="Close"
            >
              ×
            </button>
          </div>
          
          <div className="p-6">
            <div className="mb-4">
              <h3 className="text-lg font-medium text-gray-600 mb-2">
                {editingDifficulty?.associationName}
              </h3>
              <p className="text-sm text-gray-600">
                Adjust the difficulty level percentages for this {editingDifficulty?.associationType}
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-600 ">
                  Easy Questions (%)
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={tempPercentages.easy}
                  onChange={(e) => handleTempPercentageChange('easy', Number(e.target.value.replace(/[^0-9]/g, '') || 0))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:border-blue-500 focus:ring-gray-700"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-600 ">
                  Moderate Questions (%)
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={tempPercentages.moderate}
                  onChange={(e) => handleTempPercentageChange('moderate', Number(e.target.value.replace(/[^0-9]/g, '') || 0))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:border-blue-500 focus:ring-gray-700"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-600 ">
                  Difficult Questions (%)
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={tempPercentages.difficult}
                  onChange={(e) => handleTempPercentageChange('difficult', Number(e.target.value.replace(/[^0-9]/g, '') || 0))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:border-blue-500 focus:ring-gray-700"
                />
              </div>
            </div>

            <div className="mt-4 p-3 bg-gray-50 rounded-md">
              <div className="flex justify-between text-sm">
                <span className="font-medium">Total:</span>
                <span className={`font-bold ${
                  tempPercentages.easy + tempPercentages.moderate + tempPercentages.difficult === 100 
                    ? 'text-green-600' 
                    : 'text-red-600'
                }`}>
                  {tempPercentages.easy + tempPercentages.moderate + tempPercentages.difficult}%
                </span>
              </div>
              {tempPercentages.easy + tempPercentages.moderate + tempPercentages.difficult !== 100 && (
                <p className="text-xs text-red-600 mt-1">
                  Percentages must add up to exactly 100%
                </p>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
            <button
              onClick={handleCloseDifficultyEdit}
              className="px-4 py-2 text-gray-600 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors shadow-md active:scale-95"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveDifficultyEdit}
              disabled={tempPercentages.easy + tempPercentages.moderate + tempPercentages.difficult !== 100}
              className={`px-4 py-2 rounded-md transition-colors ${
                tempPercentages.easy + tempPercentages.moderate + tempPercentages.difficult === 100
                  ? 'bg-[#111827] text-white hover:bg-gray-800'
                  : 'bg-gray-300 text-gray-600 cursor-not-allowed'
              }`}
            >
              Save Changes
            </button>
          </div>
        </div>
      </ReactModal>
    </MainLayout>
  );
};

export default TeacherOfflineCustomizeExamPage; 
